import { createEffect, createSignal, untrack } from 'solid-js';
import { useTranslation } from '../hooks/useTranslation';

const GROUP_SEPARATOR = '.';
const DECIMAL_SEPARATOR = ',';
const DEFAULT_MAX_DECIMALS = 2;
const NOT_TYPED_CHAR = /[^0-9,]/g;
const ABBREVIATED = /^(.*\d[.,\d]*)\s*([km])$/i;
const ABBREVIATION_FACTOR: Record<string, number> = { k: 1_000, m: 1_000_000 };

function parseTyped(display: string): number | '' {
    const typed = display.replace(NOT_TYPED_CHAR, '');
    if (typed === '' || typed === DECIMAL_SEPARATOR) return '';
    const parsed = Number(typed.replace(DECIMAL_SEPARATOR, '.'));
    return Number.isFinite(parsed) ? parsed : '';
}

/** "50k" is 50.000 and "1,5m" is 1.500.000; null when the text does not end in k or m. */
export function expandAbbreviation(raw: string, maxDecimals = DEFAULT_MAX_DECIMALS): number | null {
    const match = ABBREVIATED.exec(raw.trim());
    if (!match) return null;
    const amount = parseTyped(match[1]);
    if (amount === '') return null;
    const scale = 10 ** maxDecimals;
    return Math.round(amount * ABBREVIATION_FACTOR[match[2].toLowerCase()] * scale) / scale;
}

function formatTyped(display: string, maxDecimals: number): string {
    const typed = display.replace(NOT_TYPED_CHAR, '');
    const separatorIndex = typed.indexOf(DECIMAL_SEPARATOR);
    const wholeDigits = (separatorIndex === -1 ? typed : typed.slice(0, separatorIndex)).replace(/^0+(?=\d)/, '');
    const grouped = groupThousands(wholeDigits);
    if (separatorIndex === -1) return grouped;
    const decimalDigits = typed.slice(separatorIndex + 1).replace(NOT_TYPED_CHAR, '').replace(/,/g, '').slice(0, maxDecimals);
    return `${grouped === '' ? '0' : grouped}${DECIMAL_SEPARATOR}${decimalDigits}`;
}

function groupThousands(digits: string): string {
    let grouped = '';
    for (let position = 0; position < digits.length; position++) {
        if (position > 0 && (digits.length - position) % 3 === 0) grouped += GROUP_SEPARATOR;
        grouped += digits[position];
    }
    return grouped;
}

function toDisplay(value: number | '', maxDecimals: number): string {
    return value === '' ? '' : formatTyped(String(value).replace('.', DECIMAL_SEPARATOR), maxDecimals);
}

function countTypedChars(text: string): number {
    return text.replace(NOT_TYPED_CHAR, '').length;
}

function caretAfterTypedChars(display: string, typedChars: number): number {
    if (typedChars === 0) return 0;
    let seen = 0;
    for (let position = 0; position < display.length; position++) {
        if (display[position] !== GROUP_SEPARATOR) seen++;
        if (seen === typedChars) return position + 1;
    }
    return display.length;
}

interface CurrencyInputProps {
    value: number | '';
    onChange: (value: number | '') => void;
    placeholder?: string;
    class?: string;
    'aria-label'?: string;
    maxDecimals?: number;
}

export default function CurrencyInput(props: CurrencyInputProps) {
    const { t } = useTranslation();
    const maxDecimals = () => props.maxDecimals ?? DEFAULT_MAX_DECIMALS;
    const [display, setDisplay] = createSignal(toDisplay(props.value, maxDecimals()));

    createEffect(() => {
        const incoming = props.value;
        untrack(() => {
            if (parseTyped(display()) === incoming) return;
            setDisplay(toDisplay(incoming, maxDecimals()));
        });
    });

    const applyEdit = (input: HTMLInputElement, rawValue: string, caret: number) => {
        const expanded = expandAbbreviation(rawValue, maxDecimals());
        if (expanded !== null) {
            const expandedDisplay = toDisplay(expanded, maxDecimals());
            setDisplay(expandedDisplay);
            input.value = expandedDisplay;
            input.setSelectionRange(expandedDisplay.length, expandedDisplay.length);
            props.onChange(expanded);
            return;
        }

        const typedBeforeCaret = countTypedChars(rawValue.slice(0, caret));
        const formatted = formatTyped(rawValue, maxDecimals());
        setDisplay(formatted);
        if (input.value !== formatted) input.value = formatted;
        const restoredCaret = caretAfterTypedChars(formatted, typedBeforeCaret);
        input.setSelectionRange(restoredCaret, restoredCaret);
        props.onChange(parseTyped(formatted));
    };

    const handleKeyDown = (e: KeyboardEvent & { currentTarget: HTMLInputElement }) => {
        const input = e.currentTarget;
        const caret = input.selectionStart ?? 0;
        if (e.key !== 'Backspace' || caret === 0 || caret !== input.selectionEnd) return;
        if (input.value[caret - 1] !== GROUP_SEPARATOR) return;
        e.preventDefault();
        applyEdit(input, input.value.slice(0, caret - 2) + input.value.slice(caret), caret - 2);
    };

    return (
        <input
            type="text"
            inputMode="text"
            title={t('CURRENCY_INPUT_ABBREVIATIONS')}
            class={props.class}
            aria-label={props['aria-label']}
            placeholder={props.placeholder}
            value={display()}
            onKeyDown={handleKeyDown}
            onInput={(e) => applyEdit(e.currentTarget, e.currentTarget.value, e.currentTarget.selectionStart ?? e.currentTarget.value.length)}
        />
    );
}
